/* eslint-disable */
import React from 'react';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { windowStore } from 'util/windowStore';
import { cacheKey, PRODUCT_BASE_PATH, authTypes, loggerLevel } from 'appConstants';
import { sendToLogger } from 'logger';
import config from 'config';

export let auth0Client = null;

export const useAuth0 = () => {
  const [auth0ClientInitiated, setAuth0ClientInitiated] = React.useState(false);

  const configureAuth0Client = (auth0) => {
    if (auth0) {
      const {
        auth: { serverUrl, organization, iamRegion },
      } = auth0;
      const { clientId, domain } = config.auth0[iamRegion];
      const iamDomain = serverUrl ? serverUrl.split('://')[1] : domain;

      auth0Client = new Auth0Client({
        organization,
        domain: iamDomain,
        client_id: clientId,
        scope: config.auth0.scope,
        audience: config.auth0.audience,
        redirect_uri: PRODUCT_BASE_PATH,
        responseType: 'code id_token',
        useRefreshTokens: true,
        cacheLocation: 'localstorage',
      });

      /**
       * auth0 is a flag set to true for users who log in via IAM.
       * This flag is used to swap auth-tokens in axios-request-interceptor (see util/Api/config.js)
       */
      windowStore.auth0 = true;
      setAuth0ClientInitiated(true);
    }
  };

  React.useEffect(() => {
    /**
     * localStorage will have "providers" and/or "@@auth0spajs@@" cached data.
     *   - When user is redirected to /iam the auth0 data is available in "providers"
     *   - When user is redirected to any other path, the auth0 data is available via @@auth0spajs@@ prefix
     */
    const cachedProviders = windowStore.get(cacheKey.PROVIDERS) || [];
    const cachedAuth0Data = Object.keys(localStorage).find((item) =>
      item.includes(cacheKey.AUTH0_PREFIX),
    );

    const parsedAuth0Data = JSON.parse(localStorage.getItem(cachedAuth0Data));

    let auth0DataViaAuth0SDK;
    if (parsedAuth0Data && parsedAuth0Data.body.decodedToken) {
      const {
        decodedToken: { claims },
      } = parsedAuth0Data.body;
      const stripped = claims.iss.endsWith('/') && claims.iss.slice(0, claims.iss.length - 1);

      let iamRegion = 'US';
      if (claims.iss.endsWith('ca/')) {
        iamRegion = 'CA';
      } else if (claims.iss.endsWith('eu/')) {
        iamRegion = 'EU';
      }
      auth0DataViaAuth0SDK = {
        auth: {
          serverUrl: stripped,
          organization: claims.org_id,
          iamRegion,
        },
      };
    }
    const auth0DataFromProviders = cachedProviders.find(
      (auth) => auth.authType === authTypes.AUTH0,
    );

    const auth0Data = auth0DataFromProviders || auth0DataViaAuth0SDK;

    configureAuth0Client(auth0Data);
  }, []);

  const login = ({ auth0, username = '' }) => {
    /**
     * onLogin flag is set to true for users who log in via IAM.
     * This flag will help prevent authentication for the rare case where
     * the user has logged out, attempts to log back, and the Auth0-SDK
     * provides valid access token. -Amir
     */
    configureAuth0Client(auth0);
    try {
      /**
       * This is temporary but necessary. Since the React app and Angular app
       * share the same Auth0 instance, this sessionStorage flag is necessary to
       * redirect users to the correct application. Will remove post summer release :) -Elpi
       */
      sessionStorage.setItem(cacheKey.WEB_REACT_IAM, true);
      auth0Client.loginWithRedirect({
        redirect_uri: `${PRODUCT_BASE_PATH}/#/iam`,
        login_hint: username,
      });
    } catch (error) {
      sendToLogger({
        level: loggerLevel.FATAL,
        message: {
          message: 'Auth0 authenticate error',
          userAgent: window.location.userAgent,
          err: error,
        },
      });
    }
  };

  return {
    auth0ClientInitiated,
    login,
    auth0: auth0Client,
  };
};
